import { defineStore } from 'pinia'
import { signOut } from 'firebase/auth'
import { router } from '@/router'
import { auth } from '@/router/firebase'
import { clearGeoToken } from '@/services/geoToken'
import { clearS3Session } from '@/services/s3Session'
import { stores } from '@/stores'
import { useChatStore } from '@/stores/chat'
import { useUserStore } from '@/stores/user'
import { useOrgStore } from '@/stores/org'

// Kept across logout: session flags, and the user store for its device settings.
const KEPT_ON_LOGOUT = new Set(['app', 'user'])

// App-wide session flags: Firebase auth bootstrap and platform login.
export const useAppStore = defineStore('app', {
  state: () => ({
    authInitialized: false,
    isLoggedIn: false,
    isLoadingData: false,
    firebaseToken: null as string | null,
  }),

  actions: {
    setAuthInitialized() {
      this.authInitialized = true
    },
    setIsLoggedIn(isLoggedIn: boolean) {
      this.isLoggedIn = isLoggedIn
    },
    setIsLoadingData(isLoadingData: boolean) {
      this.isLoadingData = isLoadingData
    },
    setFirebaseToken(token: string | null) {
      this.firebaseToken = token
    },

    async logout() {
      // Sign out from Firebase
      await signOut(auth)

      // Clear local storage
      localStorage.removeItem('accessToken')
      localStorage.removeItem('refreshToken')
      localStorage.removeItem('selectedProject')
      localStorage.removeItem('selectedRegion')

      // Clear store state
      const user = useUserStore()
      useOrgStore().setSelectedOrg(null)
      user.setUser(null)
      user.setMember(null)
      this.isLoggedIn = false
      // Every org-scoped store would otherwise hand the previous user's data
      // to the next login. chat.reset() also clears its module-level timers.
      useChatStore().reset()
      for (const store of stores) {
        if (!KEPT_ON_LOGOUT.has(store.$id)) store.$reset()
      }
      clearS3Session()
      clearGeoToken()

      // Redirect to auth page (unless on invite page)
      if (window.location.pathname.includes('/invite')) {
        return
      }
      router.push('/auth').catch(()=>{})
    },
  },
})
